import * as actionTypes from '../../constants/ActionTypes';
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activeStep: 0,
  courseDetails: null,
  cdlType: null,
  endorsements: null,
  moreClassSelection: [],
  stateSelection: null,
  videoSelection: null,
  courseRecommendation: null,
  screenData: {
    'course-selection': null,
    select: null,
    endorsements: null,
    video: null,
    recommend: null,
    cdl_type: null,
  },
  dynamicData: null,
  dynamicDataLoading: false,
  dynamicDataError: null,
  navigationPath: null,
  currentStep: null,
  previousStep: null,
  steps: ['', '', '', '', ''],
  isBackNavigation: false,
  navigationHistory: [],
  isManualBackNavigation: false,
  queryParams: {},
  directAccess: {
    data: null,
    loading: false,
    error: null
  },
};

const wizardLaunchSlice = createSlice({
  name: 'wizardLaunch',
  initialState,
  reducers: {
    updateFormState: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    resetDynamicData: (state) => {
      state.dynamicData = null;
      state.dynamicDataLoading = false;
      state.dynamicDataError = null;
    },
    setDynamicData: (state, action) => {
      // Existing implementation...
    },
    setSteps: (state, action) => {
      state.steps = action.payload;
    },
    setDirectAccessData: (state, action) => {
      if (!state.directAccess) {
        state.directAccess = {
          data: null,
          loading: false,
          error: null
        };
      }
      state.directAccess.data = action.payload;
      state.directAccess.loading = false;
      state.directAccess.error = null;
    },
    setDirectAccessLoading: (state, action) => {
      if (!state.directAccess) {
        state.directAccess = {
          data: null,
          loading: false,
          error: null
        };
      }
      state.directAccess.loading = action.payload;
    },
    setDirectAccessError: (state, action) => {
      if (!state.directAccess) {
        state.directAccess = {
          data: null,
          loading: false,
          error: null
        };
      }
      state.directAccess.error = action.payload;
      state.directAccess.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(actionTypes.NEXT_STEP, (state) => {
        state.activeStep += 1;
      })
      .addCase(actionTypes.PREV_STEP, (state) => {
        state.activeStep -= 1;
      })
      .addCase(actionTypes.SET_COURSE_DETAILS, (state, action) => {
        state.courseDetails = action.payload;
      })
      .addCase(actionTypes.SET_CDL_TYPE, (state, action) => {
        state.cdlType = action.payload;
      })
      .addCase(actionTypes.SET_ENDORSEMENTS, (state, action) => {
        state.endorsements = action.payload;
      })
      .addCase(actionTypes.SET_DIRECT_COURSES, (state, action) => {
        state.directCourses = action.payload;
      })
      .addCase(actionTypes.SET_STATE_SELECTION, (state, action) => {
        state.stateSelection = action.payload;
      })
      .addCase(actionTypes.SET_VIDEO_SELECTION, (state, action) => {
        state.videoSelection = action.payload;
      })
      .addCase(actionTypes.SET_SELECTED_COURSES, (state, action) => {
        state.moreClassSelection = action.payload;
      })
      .addCase(actionTypes.REMOVE_SELECTED_COURSE, (state, action) => {
        state.moreClassSelection = state.moreClassSelection.filter(
          (_, index) => index !== action.payload
        );
      })
      .addCase(actionTypes.REMOVE_DIRECT_COURSE, (state, action) => {
        state.directCourses = state.directCourses.filter(
          (_, index) => index !== action.payload
        );
      })
      .addCase(actionTypes.SET_COURSE_RECOMMENDATION, (state, action) => {
        state.courseRecommendation = action.payload;
      })
      .addCase(actionTypes.FETCH_DYNAMIC_DATA_REQUEST, (state) => {
        state.dynamicDataLoading = true;
        state.dynamicDataError = null;
      })
      .addCase(actionTypes.FETCH_DYNAMIC_DATA_SUCCESS, (state, action) => {
        state.dynamicDataLoading = false;
        state.dynamicData = action.payload;
        state.dynamicDataError = null;

        if (action.payload?.question_type) {
          // Ensure answer is always an array
          const answer = Array.isArray(action.payload.answer)
            ? action.payload.answer
            : action.payload.answer
            ? [action.payload.answer]
            : [];

          // Preserve existing userAnswer if available
          const existingScreenData =
            state.screenData[action.payload.question_type];
          const userAnswer =
            existingScreenData?.userAnswer ||
            state.dynamicData?.userAnswer ||
            null;

          state.screenData[action.payload.question_type] = {
            ...action.payload,
            answer,
            userAnswer,
          };
        }
      })
      .addCase(actionTypes.FETCH_DYNAMIC_DATA_FAILURE, (state, action) => {
        state.dynamicDataLoading = false;
        state.dynamicDataError = action.payload;
      })
      .addCase(actionTypes.SET_DYNAMIC_DATA, (state, action) => {
        state.dynamicData = action.payload;
        state.dynamicDataLoading = false;
        state.dynamicDataError = null;

        if (action.payload?.question_type) {
          // Ensure answer is always an array
          const answer = Array.isArray(action.payload.answer)
            ? action.payload.answer
            : action.payload.answer
            ? [action.payload.answer]
            : [];

          // Use provided userAnswer or preserve existing one
          const existingScreenData =
            state.screenData[action.payload.question_type];
          const userAnswer =
            action.payload?.userAnswer ||
            existingScreenData?.userAnswer ||
            null;

          state.screenData[action.payload.question_type] = {
            ...action.payload,
            answer,
            userAnswer,
          };
        }
      })
      .addCase(actionTypes.RESET_DYNAMIC_DATA, (state) => {
        state.dynamicData = null;
        state.dynamicDataLoading = false;
        state.dynamicDataError = null;
      })
      .addCase(actionTypes.REMOVE_ENDORSEMENT, (state, action) => {
        // Handle remove endorsement
        if (state.endorsements) {
          state.endorsements = state.endorsements.filter(
            (_, index) => index !== action.payload
          );
        }
      })
      .addCase('SET_IS_BACK_NAVIGATION', (state, action) => {
        return {
          ...state,
          isBackNavigation: action.payload,
        };
      })
      .addCase('SET_NAVIGATION_HISTORY', (state, action) => {
        return {
          ...state,
          navigationHistory: Array.isArray(action.payload)
            ? action.payload
            : [],
        };
      })
      .addCase('ADD_TO_NAVIGATION_HISTORY', (state, action) => {
        // Ensure navigationHistory is an array
        const history = Array.isArray(state.navigationHistory)
          ? state.navigationHistory
          : [];

        // Check if the path is already the last item in the history
        if (
          history.length === 0 ||
          history[history.length - 1] !== action.payload
        ) {
          return {
            ...state,
            navigationHistory: [...history, action.payload],
          };
        }
        return state;
      })
      .addCase('REMOVE_LAST_FROM_HISTORY', (state) => {
        // Safety check for navigationHistory
        const history = Array.isArray(state.navigationHistory)
          ? state.navigationHistory
          : [];

        return {
          ...state,
          navigationHistory: history.length > 0 ? history.slice(0, -1) : [],
        };
      })
      .addCase('SET_MANUAL_BACK_NAVIGATION', (state, action) => {
        return {
          ...state,
          isManualBackNavigation: action.payload,
        };
      })
      .addCase('SET_PREVIOUS_STEP', (state, action) => {
        state.previousStep = action.payload;
      })
      .addCase('UPDATE_NAVIGATION_STATE', (state, action) => {
        return {
          ...state,
          navigationPath: action.payload.navigationPath,
          currentStep: action.payload.currentStep,
          previousStep: action.payload.previousStep,
        };
      })
      .addCase('TRACK_NAVIGATION_SEQUENCE', (state, action) => {
        const { currentPath, nextPath } = action.payload;

        // Create a clean history array
        const history = Array.isArray(state.navigationHistory)
          ? [...state.navigationHistory]
          : [];

        // First, ensure we have the current path in history (without duplicates)
        if (
          history.length === 0 ||
          history[history.length - 1] !== currentPath
        ) {
          history.push(currentPath);
        }

        // Update the navigation state
        return {
          ...state,
          navigationPath: nextPath,
          currentStep: nextPath,
          previousStep: currentPath,
          navigationHistory: history,
        };
      })
      .addCase('GO_BACK_IN_NAVIGATION', (state, action) => {
        const { navigationPath, currentStep, previousStep } = action.payload;

        // Update history by removing paths after the current one if needed
        let history = Array.isArray(state.navigationHistory)
          ? [...state.navigationHistory]
          : [];

        // Special case for going back to home
        if (navigationPath === '/' || navigationPath === '/course-selection') {
          return {
            ...state,
            navigationPath,
            currentStep,
            previousStep: '/',
            isBackNavigation: true,
            navigationHistory: [], // Empty history when at first screen
          };
        }

        return {
          ...state,
          navigationPath,
          currentStep,
          previousStep,
          isBackNavigation: true,
          navigationHistory: history,
        };
      })
      .addCase('NEXT_STEP', (state, action) => {
        state.activeStep += 1;
      })
      .addCase('PREV_STEP', (state) => {
        state.activeStep -= 1;
      })
      .addCase('RESET_STEP', (state) => {
        state.activeStep = 0;
      })
      .addCase(actionTypes.SET_STEPS, (state, action) => {
        state.steps = action.payload;
      })
      .addCase(actionTypes.CLEAR_ENDORSEMENTS, (state) => {
        state.endorsements = [];
      })
      .addCase(actionTypes.CLEAR_MORE_CLASS_SELECTION, (state) => {
        state.moreClassSelection = [];
      })
      .addCase(actionTypes.SET_QUERY_PARAMS, (state, action) => {
        return {
          ...state,
          queryParams: action.payload,
        };
      })
      .addCase('SET_DIRECT_ACCESS_DATA_SUCCESS', (state, action) => {
        if (!state.directAccess) {
          state.directAccess = {
            data: null,
            loading: false,
            error: null
          };
        }
        state.directAccess.data = action.payload;
        state.directAccess.loading = false;
        state.directAccess.error = null;
      })
      .addCase('SET_DIRECT_ACCESS_LOADING', (state, action) => {
        if (!state.directAccess) {
          state.directAccess = {
            data: null,
            loading: false,
            error: null
          };
        }
        state.directAccess.loading = action.payload;
      })
      .addCase('SET_DIRECT_ACCESS_ERROR', (state, action) => {
        if (!state.directAccess) {
          state.directAccess = {
            data: null,
            loading: false,
            error: null
          };
        }
        state.directAccess.error = action.payload;
        state.directAccess.loading = false;
      });
  },
});

export const {
  resetDynamicData,
  setDynamicData,
  updateFormState,
  setCurrentStep,
  setSteps,
  setDirectAccessData,
  setDirectAccessLoading,
  setDirectAccessError,
} = wizardLaunchSlice.actions;

export default wizardLaunchSlice.reducer;
